import api from './api';

// Types matching backend DTOs
export interface User {
  id: number;
  oktaId: string;
  organizationId: number | null;
  organizationName: string | null;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  title: string | null;
  department: string | null;
  location: string | null;
  phone: string | null;
  phoneCountryCode: string | null;
  userType: string;
  platformRole: string;
  employmentStatus: string;
  createdAt: string;
  updatedAt: string | null;
  lastLoginAt: string | null;
}

// Response from POST /api/users — includes the created user and a Beacon provisioning flag
export interface CreateUserResponse {
  user: User;
  beaconProvisioned: boolean;
}

// Note: oktaId is auto-generated by the backend via Okta API, not provided by the client
export interface CreateUserGroupRef {
  /** Existing group id within the same organization. */
  id?: number;
  /** Group name; matched case-insensitively to an existing group, or auto-created if missing. */
  name?: string;
}

export interface CreateUser {
  organizationId?: number | null;
  email: string;
  firstName: string;
  lastName: string;
  title?: string | null;
  department?: string | null;
  location?: string | null;
  userType?: string;
  platformRole?: string;
  employmentStatus?: string;
  /** Org-scoped groups to assign at creation time. Honored by POST /organizations/{orgId}/users. */
  groups?: CreateUserGroupRef[];
  /**
   * Ids of the organization's Active contracts to put the new user on once they exist (BPE-769).
   * Honored by POST /organizations/{orgId}/users. Contracts of the same type must not overlap in dates.
   */
  contractIds?: number[];
}

export interface UpdateUser {
  organizationId?: number | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  title?: string | null;
  department?: string | null;
  location?: string | null;
  phone?: string | null;
  phoneCountryCode?: string | null;
  userType?: string | null;
  platformRole?: string | null;
  salesforceContactId?: string | null;
  beaconUserId?: string | null;
  employmentStatus?: string | null;
}

export interface UserQuery {
  page?: number;
  limit?: number;
  search?: string;
  organizationId?: number;
  /** Comma-separated for multi-select (e.g. "Learner,OrgManager"). BPE-363. */
  platformRole?: string;
  /** Comma-separated for multi-select (e.g. "Active,OnLeave"). BPE-363. */
  employmentStatus?: string;
  userType?: string;
  /** Comma-separated for multi-select. BPE-363. */
  department?: string;
  /** Comma-separated for multi-select. BPE-363. */
  location?: string;
  sortBy?: string;
  sortDesc?: boolean;
}

export interface UserFilterOptions {
  departments: string[];
  locations: string[];
}

export interface PagedResult<T> {
  items: T[];
  page: number;
  limit: number;
  totalCount: number;
}

export interface CurrentUser {
  id: number;
  oktaId: string;
  organizationId: number | null;
  organizationName: string | null;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  title: string | null;
  department: string | null;
  location: string | null;
  phone: string | null;
  phoneCountryCode: string | null;
  userType: string;
  platformRole: string;
  employmentStatus: string;
}

export interface UpdateMyProfile {
  firstName?: string | null;
  lastName?: string | null;
  title?: string | null;
  department?: string | null;
  location?: string | null;
  phone?: string | null;
  phoneCountryCode?: string | null;
}

export interface UpdateUserJurisdictionDto {
  licenseNumber?: string;
  admissionDate?: string;
  expirationDate?: string;
  status?: 'active' | 'inactive' | 'suspended' | 'retired';
}

export interface UpdateUserPracticeAreaDto {
  isPrimary: boolean;
}

export interface UpdateUserSkillDto {
  level: 'novice' | 'beginner' | 'intermediate' | 'expert';
}

// API response wrapper type
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// User attribute types for Super Admin management
export interface UserJurisdictionDto {
  id: number;
  jurisdictionId: number;
  jurisdictionName: string;
  jurisdictionAbbr: string;
  credentialType: 'CLE' | 'CPE' | 'IAPP' | 'EA';
  licenseNumber: string;
  admissionDate?: string;
  expirationDate?: string;
  status: 'active' | 'inactive' | 'suspended' | 'retired';
}

export interface AddUserJurisdictionDto {
  credentialType: 'CLE' | 'CPE' | 'IAPP' | 'EA';
  jurisdiction: string;
  licenseNumber: string;
  admissionDate?: string;
  expirationDate?: string;
}

export interface UserPracticeAreaDto {
  id: number;
  practiceAreaId: number;
  name: string;
  category?: string;
  isPrimary: boolean;
}

export interface AddUserPracticeAreaDto {
  practiceAreaId: number;
  isPrimary: boolean;
}

export interface UserSkillDto {
  id: number;
  skillId: number;
  name: string;
  level: 'novice' | 'beginner' | 'intermediate' | 'expert';
}

export interface AddUserSkillDto {
  skillId: number;
  level?: 'novice' | 'beginner' | 'intermediate' | 'expert';
}

// Assignment types for Super Admin user detail
export interface UserAssignmentDto {
  id: number;
  itemType: 'course' | 'pathway';
  itemId: number;
  itemTitle: string;
  durationMinutes: number;
  assignedAt: string;
  dueDate?: string;
  status: string;
  isOverdue: boolean;
  assignedByUserId?: number;
  assignedByUserName?: string;
}

export interface AssignCourseToUserDto {
  learningObjectId: number;
  dueDate?: string;
}

export interface AssignPathwayToUserDto {
  pathwayId: number;
  dueDate?: string;
}

// Group types for Super Admin user detail
export interface UserGroupMembershipDto {
  id: number;
  name: string;
  organizationId: number;
  organizationName: string;
  isActive: boolean;
  addedAt: string;
}

// Activity feed item — shape matches PE.Application.DTOs.ActivityDto.
// Used by the History tab on the super-admin user-detail page.
export interface UserActivityDto {
  id: number;
  type: string;
  description: string;
  timestamp: string;
  learningObjectId: number | null;
  learningObjectTitle: string | null;
  pathwayId: number | null;
  pathwayTitle: string | null;
  certificateId: number | null;
  webinarId: number | null;
  icon: string;
}

// One row of the Attendances tab — matches PE.Application.DTOs.UserAttendanceDto.
export interface UserAttendanceDto {
  id: number;
  learningObjectId: number;
  learningObjectTitle: string;
  /** Credential code uppercased (CLE, CPE, IAPP, EA) — empty for non-credentialed content. */
  courseType: string;
  completedAt: string | null;
  surveyCompletedAt: string | null;
  isOverride: boolean;
  overrideReason: string | null;
}

export const usersApi = {
  // ============================================================================
  // Super Admin Endpoints: /api/users
  // ============================================================================

  /**
   * Get all users with pagination, search, and filters (SuperAdmin only)
   */
  getAll: async (params?: UserQuery): Promise<PagedResult<User>> => {
    const response = await api.get<PagedResult<User>>('/users', { params });
    return response.data;
  },

  /**
   * Get user by ID (SuperAdmin only)
   */
  getById: async (id: number): Promise<User> => {
    const response = await api.get<User>(`/users/${id}`);
    return response.data;
  },

  /**
   * Create a new user (SuperAdmin only).
   * Returns the created user and a flag indicating whether Beacon provisioning succeeded.
   */
  create: async (user: CreateUser): Promise<CreateUserResponse> => {
    const response = await api.post<CreateUserResponse>('/users', user);
    return response.data;
  },

  /**
   * Update a user (SuperAdmin only)
   */
  update: async (id: number, user: UpdateUser): Promise<User> => {
    const response = await api.put<User>(`/users/${id}`, user);
    return response.data;
  },

  /**
   * Delete a user (SuperAdmin only)
   */
  delete: async (id: number): Promise<void> => {
    await api.delete(`/users/${id}`);
  },

  // ============================================================================
  // Org Admin Endpoints: /api/organizations/{orgId}/users
  // ============================================================================

  /**
   * Get users for an organization (OrgAdmin only)
   */
  getOrgUsers: async (orgId: number, params?: Omit<UserQuery, 'organizationId'>): Promise<PagedResult<User>> => {
    const response = await api.get<PagedResult<User>>(`/organizations/${orgId}/users`, { params });
    return response.data;
  },

  /**
   * Distinct department + location values across the org's users. BPE-363:
   * powers the org-admin Users page filter drawer dropdowns so options outside
   * the current page are still selectable.
   */
  getOrgUserFilterOptions: async (orgId: number): Promise<UserFilterOptions> => {
    const response = await api.get<UserFilterOptions>(`/organizations/${orgId}/users/filter-options`);
    return response.data;
  },

  /**
   * Get organization user by ID (OrgAdmin only)
   */
  getOrgUserById: async (orgId: number, id: number): Promise<User> => {
    const response = await api.get<User>(`/organizations/${orgId}/users/${id}`);
    return response.data;
  },

  /**
   * Add user to organization (OrgAdmin only)
   */
  createOrgUser: async (orgId: number, user: CreateUser): Promise<User> => {
    const response = await api.post<User>(`/organizations/${orgId}/users`, user);
    return response.data;
  },

  /**
   * Update organization user (OrgAdmin only)
   */
  updateOrgUser: async (orgId: number, id: number, user: UpdateUser): Promise<User> => {
    const response = await api.put<User>(`/organizations/${orgId}/users/${id}`, user);
    return response.data;
  },

  /**
   * Remove user from organization (OrgAdmin only)
   */
  deleteOrgUser: async (orgId: number, id: number): Promise<void> => {
    await api.delete(`/organizations/${orgId}/users/${id}`);
  },

  // ============================================================================
  // Current User Endpoints: /api/me
  // ============================================================================

  /**
   * Get current user profile
   */
  getCurrentUser: async (): Promise<CurrentUser> => {
    const response = await api.get<CurrentUser>('/me');
    return response.data;
  },

  /**
   * Update current user profile (limited fields)
   */
  updateCurrentUser: async (profile: UpdateMyProfile): Promise<CurrentUser> => {
    const response = await api.put<CurrentUser>('/me', profile);
    return response.data;
  },

  /**
   * JIT provisioning - sync user from Okta token
   */
  syncCurrentUser: async (): Promise<CurrentUser> => {
    const response = await api.post<CurrentUser>('/me/sync');
    return response.data;
  },

  // ============================================================================
  // Super Admin User Attributes: /api/users/{id}/jurisdictions, practice-areas, skills
  // ============================================================================

  /**
   * Get user's jurisdictions (SuperAdmin only)
   */
  getJurisdictions: async (userId: number): Promise<UserJurisdictionDto[]> => {
    const response = await api.get<ApiResponse<{ jurisdictions: UserJurisdictionDto[] }>>(`/users/${userId}/jurisdictions`);
    return response.data.data?.jurisdictions ?? [];
  },

  /**
   * Add jurisdiction to user (SuperAdmin only)
   */
  addJurisdiction: async (userId: number, data: AddUserJurisdictionDto): Promise<number> => {
    const response = await api.post<ApiResponse<{ jurisdiction_id: number }>>(`/users/${userId}/jurisdictions`, data);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to add jurisdiction');
    }
    return response.data.data.jurisdiction_id;
  },

  /**
   * Remove jurisdiction from user (SuperAdmin only)
   */
  removeJurisdiction: async (userId: number, jurisdictionId: number): Promise<void> => {
    await api.delete(`/users/${userId}/jurisdictions/${jurisdictionId}`);
  },

  /**
   * Get user's practice areas (SuperAdmin only)
   */
  getPracticeAreas: async (userId: number): Promise<UserPracticeAreaDto[]> => {
    const response = await api.get<ApiResponse<{ practice_areas: UserPracticeAreaDto[] }>>(`/users/${userId}/practice-areas`);
    return response.data.data?.practice_areas ?? [];
  },

  /**
   * Add practice area to user (SuperAdmin only)
   */
  addPracticeArea: async (userId: number, data: AddUserPracticeAreaDto): Promise<void> => {
    const response = await api.post<ApiResponse<void>>(`/users/${userId}/practice-areas`, data);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to add practice area');
    }
  },

  /**
   * Remove practice area from user (SuperAdmin only)
   */
  removePracticeArea: async (userId: number, practiceAreaId: number): Promise<void> => {
    await api.delete(`/users/${userId}/practice-areas/${practiceAreaId}`);
  },

  /**
   * Get user's skills (SuperAdmin only)
   */
  getSkills: async (userId: number): Promise<UserSkillDto[]> => {
    const response = await api.get<ApiResponse<{ skills: UserSkillDto[] }>>(`/users/${userId}/skills`);
    return response.data.data?.skills ?? [];
  },

  /**
   * Add skill to user (SuperAdmin only)
   */
  addSkill: async (userId: number, data: AddUserSkillDto): Promise<void> => {
    const response = await api.post<ApiResponse<void>>(`/users/${userId}/skills`, data);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to add skill');
    }
  },

  /**
   * Remove skill from user (SuperAdmin only)
   */
  removeSkill: async (userId: number, skillId: number): Promise<void> => {
    await api.delete(`/users/${userId}/skills/${skillId}`);
  },

  /**
   * Update jurisdiction for user (SuperAdmin only)
   */
  updateJurisdiction: async (userId: number, id: number, data: UpdateUserJurisdictionDto): Promise<void> => {
    const response = await api.put<ApiResponse<void>>(`/users/${userId}/jurisdictions/${id}`, data);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update jurisdiction');
    }
  },

  /**
   * Update practice area for user (SuperAdmin only)
   */
  updatePracticeArea: async (userId: number, id: number, data: UpdateUserPracticeAreaDto): Promise<void> => {
    const response = await api.put<ApiResponse<void>>(`/users/${userId}/practice-areas/${id}`, data);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update practice area');
    }
  },

  /**
   * Update skill for user (SuperAdmin only)
   */
  updateSkill: async (userId: number, id: number, data: UpdateUserSkillDto): Promise<void> => {
    const response = await api.put<ApiResponse<void>>(`/users/${userId}/skills/${id}`, data);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update skill');
    }
  },

  // ============================================================================
  // Super Admin User Assignments: /api/users/{id}/assignments
  // ============================================================================

  getUserAssignments: async (userId: number): Promise<UserAssignmentDto[]> => {
    const response = await api.get<{ items: UserAssignmentDto[]; totalCount: number }>(`/users/${userId}/assignments`, { params: { limit: 100 } });
    return response.data.items ?? [];
  },

  assignCourseToUser: async (userId: number, data: AssignCourseToUserDto): Promise<void> => {
    await api.post(`/users/${userId}/assignments/learning-objects`, data);
  },

  assignPathwayToUser: async (userId: number, data: AssignPathwayToUserDto): Promise<void> => {
    await api.post(`/users/${userId}/assignments/pathways`, data);
  },

  removeUserCourseAssignment: async (userId: number, assignmentId: number): Promise<void> => {
    await api.delete(`/users/${userId}/assignments/learning-objects/${assignmentId}`);
  },

  removeUserPathwayAssignment: async (userId: number, assignmentId: number): Promise<void> => {
    await api.delete(`/users/${userId}/assignments/pathways/${assignmentId}`);
  },

  // ============================================================================
  // Super Admin User Groups: /api/users/{id}/groups
  // ============================================================================

  getUserGroups: async (userId: number): Promise<UserGroupMembershipDto[]> => {
    const response = await api.get<ApiResponse<{ groups: UserGroupMembershipDto[] }>>(`/users/${userId}/groups`);
    return response.data.data?.groups ?? [];
  },

  addUserToGroup: async (userId: number, groupId: number): Promise<void> => {
    await api.post(`/users/${userId}/groups`, { groupId });
  },

  removeUserFromGroup: async (userId: number, groupId: number): Promise<void> => {
    await api.delete(`/users/${userId}/groups/${groupId}`);
  },

  // ============================================================================
  // Super Admin User Activity & Attendances: /api/users/{id}/activity, /attendances
  // ============================================================================

  /**
   * Get a user's activity feed (SuperAdmin). Powers the History tab on the
   * super-admin user-detail page; mirrors the org-scoped variant used by org admin.
   */
  getUserActivity: async (
    userId: number,
    params: { page?: number; limit?: number; type?: string } = {},
  ): Promise<{ items: UserActivityDto[]; totalCount: number }> => {
    const response = await api.get<{ items: UserActivityDto[]; totalCount: number }>(
      `/users/${userId}/activity`,
      { params: { limit: 100, ...params } },
    );
    return response.data;
  },

  /**
   * Get a user's attendance records (SuperAdmin). Powers the Attendances tab on
   * the super-admin user-detail page. Returns one row per user/course.
   */
  getUserAttendances: async (userId: number): Promise<UserAttendanceDto[]> => {
    const response = await api.get<{ items: UserAttendanceDto[]; totalCount: number }>(
      `/users/${userId}/attendances`,
    );
    return response.data.items ?? [];
  },

  // ============================================================================
  // Current User Attributes: /api/me/jurisdictions, practice-areas, skills
  // ============================================================================

  getMyJurisdictions: async (): Promise<UserJurisdictionDto[]> => {
    const response = await api.get<ApiResponse<{ jurisdictions: UserJurisdictionDto[] }>>('/me/jurisdictions');
    return response.data.data?.jurisdictions ?? [];
  },

  addMyJurisdiction: async (data: AddUserJurisdictionDto): Promise<number> => {
    try {
      const response = await api.post<ApiResponse<{ jurisdiction_id: number }>>('/me/jurisdictions', data);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || 'Failed to add jurisdiction');
      }
      return response.data.data.jurisdiction_id;
    } catch (err: any) {
      const backendError = err?.response?.data?.error;
      throw new Error(backendError || err?.message || 'Failed to add jurisdiction');
    }
  },

  updateMyJurisdiction: async (id: number, data: UpdateUserJurisdictionDto): Promise<void> => {
    const response = await api.put<ApiResponse<void>>(`/me/jurisdictions/${id}`, data);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update jurisdiction');
    }
  },

  removeMyJurisdiction: async (id: number): Promise<void> => {
    const response = await api.delete<ApiResponse<void>>(`/me/jurisdictions/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to remove jurisdiction');
    }
  },

  getMyPracticeAreas: async (): Promise<UserPracticeAreaDto[]> => {
    const response = await api.get<ApiResponse<{ practice_areas: UserPracticeAreaDto[] }>>('/me/practice-areas');
    return response.data.data?.practice_areas ?? [];
  },

  addMyPracticeArea: async (data: AddUserPracticeAreaDto): Promise<void> => {
    const response = await api.post<ApiResponse<void>>('/me/practice-areas', data);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to add practice area');
    }
  },

  updateMyPracticeArea: async (id: number, data: UpdateUserPracticeAreaDto): Promise<void> => {
    const response = await api.put<ApiResponse<void>>(`/me/practice-areas/${id}`, data);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update practice area');
    }
  },

  removeMyPracticeArea: async (id: number): Promise<void> => {
    const response = await api.delete<ApiResponse<void>>(`/me/practice-areas/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to remove practice area');
    }
  },

  getMySkills: async (): Promise<UserSkillDto[]> => {
    const response = await api.get<ApiResponse<{ skills: UserSkillDto[] }>>('/me/skills');
    return response.data.data?.skills ?? [];
  },

  addMySkill: async (data: AddUserSkillDto): Promise<void> => {
    const response = await api.post<ApiResponse<void>>('/me/skills', data);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to add skill');
    }
  },

  updateMySkill: async (id: number, data: UpdateUserSkillDto): Promise<void> => {
    const response = await api.put<ApiResponse<void>>(`/me/skills/${id}`, data);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update skill');
    }
  },

  removeMySkill: async (id: number): Promise<void> => {
    const response = await api.delete<ApiResponse<void>>(`/me/skills/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to remove skill');
    }
  },

  // ============================================================================
  // Org Admin User Attributes: /api/organizations/{orgId}/users/{id}/jurisdictions, etc.
  // ============================================================================

  getOrgUserJurisdictions: async (orgId: number, userId: number): Promise<UserJurisdictionDto[]> => {
    const response = await api.get<ApiResponse<{ jurisdictions: UserJurisdictionDto[] }>>(`/organizations/${orgId}/users/${userId}/jurisdictions`);
    return response.data.data?.jurisdictions ?? [];
  },

  addOrgUserJurisdiction: async (orgId: number, userId: number, data: AddUserJurisdictionDto): Promise<number> => {
    const response = await api.post<ApiResponse<{ jurisdiction_id: number }>>(`/organizations/${orgId}/users/${userId}/jurisdictions`, data);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to add jurisdiction');
    }
    return response.data.data.jurisdiction_id;
  },

  updateOrgUserJurisdiction: async (orgId: number, userId: number, id: number, data: UpdateUserJurisdictionDto): Promise<void> => {
    const response = await api.put<ApiResponse<void>>(`/organizations/${orgId}/users/${userId}/jurisdictions/${id}`, data);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update jurisdiction');
    }
  },

  removeOrgUserJurisdiction: async (orgId: number, userId: number, id: number): Promise<void> => {
    const response = await api.delete<ApiResponse<void>>(`/organizations/${orgId}/users/${userId}/jurisdictions/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to remove jurisdiction');
    }
  },

  getOrgUserPracticeAreas: async (orgId: number, userId: number): Promise<UserPracticeAreaDto[]> => {
    const response = await api.get<ApiResponse<{ practice_areas: UserPracticeAreaDto[] }>>(`/organizations/${orgId}/users/${userId}/practice-areas`);
    return response.data.data?.practice_areas ?? [];
  },

  addOrgUserPracticeArea: async (orgId: number, userId: number, data: AddUserPracticeAreaDto): Promise<void> => {
    const response = await api.post<ApiResponse<void>>(`/organizations/${orgId}/users/${userId}/practice-areas`, data);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to add practice area');
    }
  },

  updateOrgUserPracticeArea: async (orgId: number, userId: number, id: number, data: UpdateUserPracticeAreaDto): Promise<void> => {
    const response = await api.put<ApiResponse<void>>(`/organizations/${orgId}/users/${userId}/practice-areas/${id}`, data);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update practice area');
    }
  },

  removeOrgUserPracticeArea: async (orgId: number, userId: number, id: number): Promise<void> => {
    const response = await api.delete<ApiResponse<void>>(`/organizations/${orgId}/users/${userId}/practice-areas/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to remove practice area');
    }
  },
};
